import Vue from 'vue';
import axios from 'axios';
import mockClaim from '../../data/claims';
import every from 'lodash/every';

const rootUrl = '/claims';

const warrantyClaims = {
  namespaced: true,

  state: {
    loading: true,
    csrf: '',
    saving: false,
    claimData: {},
    validators: [],
  },

  mutations: {
    setCSRF(state, val) {
      state.csrf = val;
    },

    setClaimData(state, claimData) {
      Vue.set(state, 'claimData', claimData);
    },

    setLoading(state, val) {
      state.loading = val;
    },

    setSaving(state, val) {
      state.saving = val;
    },

    /**
     * Generic mutation to update a property on the claimData and trigger an autosave
     * @param {Object} state
     * @param {Object} payload - Destructured to propName and value
     * @param {string} payload.propName - The name of the property that will be mutated
     * @param {string} payload.value - The value to which the property will be set
     */
    updateClaimData(state, {propName, value}) {
      state.claimData[propName] = value;
    },
  },

  actions: {
    /**
     * Generic action to update a property on the claimData and trigger an autosave
     * @param {Object} context - Destructured to commit, dispatch, and state
     * @param {Object} payload - Destructured to propName and value
     * @param {string} payload.propName - The name of the property that will be mutated
     * @param {string} payload.value - The value to which the property will be set
     */
    updateProperty({commit, dispatch, state}, {propName, value}) {
      commit('updateClaimData', {propName, value});
      dispatch('autoSaveClaim', {csrf: state.csrf});
    },

    /**
     * Autosave claimData to the backend
     * @param {Object} payload
     * @param {string} csrf - CSRF token sent through param, falls back to local state. It originates in the JSP.
     */
    autoSaveClaim({commit, state}, {csrf}) {
      // Destructure state to only the properties of it we need
      const {claimData} = state;

      // Set the parameters that will be used for the API call
      const params = {
        validate: false,
        CSRFToken: csrf || state.csrf,
      };

      // If it's a new claim (doesn't have an ID), we POST for the first autosave, then set the commit the data, which
      // includes the new claim ID
      const apiUrl = !claimData.id ? rootUrl : `${rootUrl}/${claimData.id}`;
      const apiMethod = !claimData.id ? 'post' : 'put';

      if (state.saving && !claimData.id) {
        return;
      }

      commit('setSaving', true);
      return axios[apiMethod](apiUrl, claimData, {params})
        .then((response) => {
          commit('setClaimData', response.data.data);
        })
        .finally(() => {
          commit('setSaving', false);
        });
    },

    /**
     * Validate and then/or autosave claimData to the backend
     * @param {Object} payload
     * @param {string} validate - Validation flag. Runs FE validation and informs back-end that it should do full validation on the fields
     * @param successNotificationCallback - Callback should redirect to the all-claims page upon successful completion
     */
    saveClaim({dispatch, state}, {validate, successNotificationCallback}) {
      // If validating run through all the different validators (headers, claim lines, attachments)
      if (validate) {
        let validatorPromises = [];

        // Create a promise for each validator
        state.validators.forEach((validator) => {
          validatorPromises.push(
            new Promise(function(resolve) {
              resolve(validator.validateAll());
            })
          );
        });

        // Use Promise.all to run all validator promises concurrently and catching the final result in one function in order
        // to save or ignore
        Promise.all(validatorPromises).then((values) => {
          if (every(values, (value) => value)) {
            dispatch('saveProcessedClaim', {validate: validate, csrf: state.csrf, successNotificationCallback});
          } else {
            window.vm.$scrollTo('body');
          }
        });
      } else {
        // If not validating simply save the claim as is
        dispatch('saveProcessedClaim', {validate: validate, csrf: state.csrf});
      }
    },

    /**
     * Autosave claimData to the backend
     * @param {Object} payload
     * @param {string} validate - Validation flag. Informs back-end that it should do full validation on the fields
     * @param successNotificationCallback - Callback should redirect to the all-claims page upon successful completion
     */
    saveProcessedClaim({commit, state}, {validate, successNotificationCallback}) {
      // Destructure state to only the properties of it we need
      const {claimData} = state;

      // Set the parameters that will be used for the API call
      const params = {
        validate: validate || false,
        manual: true,
      };

      return axios.put(`${rootUrl}/${claimData.id}`, claimData, {params}).then((response) => {
        if (validate && response.data.meta.feedback.type === 'success') {
          // If we're validating it means this was a submission and should redirect to the all-claims page
          // upon successful completion
          successNotificationCallback && successNotificationCallback(response.data.data.id);
        }

        commit('setClaimData', response.data.data);
      });
    },

    /**
     * Save claimData to the backend when triggered from an image input
     */
    saveImageInput({commit, state}) {
      // Destructure state to only the properties of it we need
      const {claimData} = state;

      // Set the parameters that will be used for the API call
      const params = {
        validate: false,
        manual: true,
      };

      // Artificial delay after response to ensure that Azure has time to process and make the image available
      // before we try to retrieve it.
      const azureDelay = 4000;

      commit('setLoading', true);
      commit('global/setUseToastInterceptor', false, {root: true});
      return axios
        .put(`${rootUrl}/${claimData.id}`, claimData, {params})
        .then((response) => {
          commit('setLoading', false);
          setTimeout(() => {
            commit('setClaimData', response.data.data);
          }, azureDelay);
        })
        .finally(() => {
          commit('setLoading', false);
          commit('global/setUseToastInterceptor', true, {root: true});
        });
    },

    /**
     * Fetch claim data from the API
     * @param {Object} context - Destructured to commit only
     * @param {string} claimId - The ID of the claim to fetch
     */
    fetchClaimData({commit}, claimId) {
      commit('setLoading', true);

      return axios.get(`${rootUrl}/${claimId}`).then((response) => {
        if (response && response.data && response.data.data) {
          // For testing purposes: Set mock claimLines and any other defaults we want
          if (response.data.data.status === 'enumClaimStatusNew') {
            response.data.data.claimLines = mockClaim.claimLines;
          }

          commit('setClaimData', response.data.data);
          commit('setLoading', false);
        }
      });
    },
  },
};

export default warrantyClaims;
